import { mapLines } from "./lines";

// The five identifier cases are one rebuild from one reading of the words; only the joint and each word's shape differ.
export function toIdentifierCase(text: string, operation: string): string {
  return mapLines(text, (line) => {
    const parts = words(line);
    if (parts.length === 0) return "";
    switch (operation) {
      case "camel":
        return parts.map((word, index) => index === 0 ? word.toLowerCase() : capitalise(word)).join("");
      case "pascal":
        return parts.map(capitalise).join("");
      case "snake":
        return parts.join("_").toLowerCase();
      case "constant":
        return parts.join("_").toUpperCase();
      default:
        return parts.join("-").toLowerCase();
    }
  });
}

// Prose keeps every character it had and only the letters move: a title is the text somebody wrote, capitalised.
export function toTitleCase(text: string, variant: string): string {
  return mapLines(
    text,
    // Split with the break captured, so the phrases are the even pieces and the punctuation between them the odd ones.
    (line) => line.split(PHRASE_BREAK).map((piece, index) => index % 2 ? piece : titlePhrase(piece, variant)).join(""),
  );
}

function titlePhrase(phrase: string, variant: string): string {
  const last = (phrase.match(TITLE_WORD) ?? []).length - 1;
  let index = 0;
  return phrase.replace(TITLE_WORD, (word) => {
    const position = index++;
    const lower = word.toLowerCase();
    // A headline's first and last words are capitalised whatever they are; nothing else is a rule anybody agrees on.
    if (variant === "headline" && position !== 0 && position !== last && MINOR.has(lower)) return lower;
    return capitalise(lower);
  });
}

export function toSentenceCase(text: string): string {
  return mapLines(
    text,
    (line) => line.toLowerCase().replace(SENTENCE_START, (_, lead, letter) => lead + letter.toUpperCase()),
  );
}

// An acronym, a capitalised or lowercase word, a run of digits, and last anything else that is letters — which is
// what carries a script with no case of its own through a name written in it.
const WORD = /\p{Lu}+(?![\p{Ll}\p{M}])|\p{Lu}?[\p{Ll}\p{M}]+|\p{N}+|[\p{L}\p{M}]+/gu;

function words(line: string): string[] {
  return line.match(WORD) ?? [];
}

const TITLE_WORD = /[\p{L}\p{M}\p{N}'’]+/gu;

// A colon opens a subtitle and a question or an exclamation a new phrase, each with a first and a last word of its own.
// A full stop does not: in a title it is `vs.` or `St.` far more often than the end of anything.
const PHRASE_BREAK = /([:?!]+)/u;

// The opening letter of the line, and the one after anything that ends a sentence and whatever quotes closed with it.
const SENTENCE_START = /(^[^\p{L}]*|[.!?…]["'’)\]]*\s+)(\p{L})/gu;

// Chicago's short prepositions, articles and conjunctions, which are the words a headline leaves lowercase.
const MINOR = new Set(
  [
    "a",
    "an",
    "and",
    "as",
    "at",
    "but",
    "by",
    "for",
    "if",
    "in",
    "nor",
    "of",
    "on",
    "or",
    "per",
    "so",
    "the",
    "to",
    "up",
    "via",
    "vs",
    "yet",
  ],
);

function capitalise(word: string): string {
  const first = String.fromCodePoint(word.codePointAt(0) ?? 0);
  return first.toUpperCase() + word.slice(first.length).toLowerCase();
}
